/**
 * LobbyPage — admin entry screen (mounted at /admin).
 *
 * Flow: display-name capture → session → admin token verification → media
 * catalog grid → create room or join by code. On room create/join we
 * navigate to /room/:roomId where RoomPage takes over. Only admins
 * (verified via X-Admin-Token) see the catalogue and can create rooms.
 */
import { useState, useEffect, type FormEvent } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LIMITS, ErrorCode, AppError, type MediaSummary, type AdminRoomSummary } from '@syncreel/shared';
import { MediaCatalog, formatDuration } from '../components/MediaCatalog';
import { AuthShell } from '../components/AuthShell';
import { Wordmark } from '../components/Wordmark';
import { LibraryBrowser } from '../components/LibraryBrowser';
import { AddMedia } from '../components/AddMedia';
import { Button } from '../components/ui/Button';
import { useToast } from '../components/ui/Toast';
import { postRoom, getActiveRooms, deleteRoom, verifyAdminToken } from '../api/client';
import { useSessionStore } from '../store/session';
import { logger } from '../api/logger';
import styles from './LobbyPage.module.css';
import authStyles from '../components/AuthShell.module.css';

/** Maximum length for the join-code / room-id input. */
const JOIN_INPUT_MAX = 64;

/** How often the admin's live-rooms list refreshes. */
const ACTIVE_ROOMS_POLL_MS = 10_000;

/** LobbyPage: identity capture, admin verification, media browsing, room creation, and join-by-code. */
export function LobbyPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const user = useSessionStore((s) => s.user);
  const token = useSessionStore((s) => s.token);
  const adminToken = useSessionStore((s) => s.adminToken);
  const createSession = useSessionStore((s) => s.createSession);
  const setRoom = useSessionStore((s) => s.setRoom);
  const setAdminToken = useSessionStore((s) => s.setAdminToken);
  const clearAdminToken = useSessionStore((s) => s.clearAdminToken);

  // WHY: if we were redirected from a room page (no session), returnTo holds
  // the original path so we can navigate back after the user enters a name.
  const returnTo = (location.state as { returnTo?: string } | null)?.returnTo;

  const [displayName, setDisplayName] = useState<string>('');
  const [nameError, setNameError] = useState<string | null>(null);
  const [creatingSession, setCreatingSession] = useState<boolean>(false);

  // WHY: admin token gate state. The token is verified against the server
  // (GET /api/admin/jobs) before the catalogue is shown.
  const [adminKeyInput, setAdminKeyInput] = useState<string>('');
  const [verifyingAdmin, setVerifyingAdmin] = useState<boolean>(false);
  const [adminError, setAdminError] = useState<string | null>(null);

  const [catalogRefresh, setCatalogRefresh] = useState<number>(0);
  const [selectedMedia, setSelectedMedia] = useState<MediaSummary | null>(null);
  const [roomName, setRoomName] = useState<string>('');
  const [creatingRoom, setCreatingRoom] = useState<boolean>(false);
  // WHY: the most recently created room's invitation code, shown so the admin
  // can copy and share it with normal users.
  const [lastInvitationCode, setLastInvitationCode] = useState<string | null>(null);

  const [joinCode, setJoinCode] = useState<string>('');
  const [activeRooms, setActiveRooms] = useState<AdminRoomSummary[]>([]);
  const [terminatingId, setTerminatingId] = useState<string | null>(null);

  // WHY: the listing is admin-only (X-Admin-Token), so wait for verification.
  // Poll so rooms started by others — and their host keys — show up without a
  // manual reload.
  useEffect(() => {
    if (!token || !adminToken) return;
    let cancelled = false;
    const load = (): void => {
      getActiveRooms()
        .then((res) => {
          if (!cancelled) setActiveRooms(res.rooms);
        })
        .catch((err) => {
          logger.warn('failed to fetch active rooms', err);
        });
    };
    load();
    const timer = setInterval(load, ACTIVE_ROOMS_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [token, adminToken]);

  function handleCopyHostKey(hostKey: string): void {
    navigator.clipboard
      .writeText(hostKey)
      .then(() => toast.success('Host key copied.'))
      .catch(() => toast.error('Could not copy host key.'));
  }

  // WHY: seed the session store with the room's host key and ask RoomPage to
  // claim host once the socket has joined (see RoomStage's claimHost state).
  function handleTakeOver(r: AdminRoomSummary): void {
    if (!r.hostKey) return;
    setRoom({ room: r, role: 'viewer', mediaDetail: null, joinCode: null, hostKey: r.hostKey });
    navigate(`/room/${r.id}`, { state: { claimHost: true } });
  }

  async function handleTerminateRoom(e: FormEvent, roomId: string) {
    e.preventDefault();
    setTerminatingId(roomId);
    try {
      await deleteRoom(roomId);
      setActiveRooms((prev) => prev.filter((r) => r.id !== roomId));
      toast.success('Room terminated.');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to terminate room.';
      toast.error(message);
    } finally {
      setTerminatingId(null);
    }
  }

  // ── Step 1: capture display name and create an anonymous session ──────────
  async function handleCreateSession(e: FormEvent) {
    e.preventDefault();
    const trimmed = displayName.trim();
    if (trimmed.length < 1) {
      setNameError('Display name is required.');
      return;
    }
    if (trimmed.length > LIMITS.DISPLAY_NAME_MAX) {
      setNameError(`Display name must be ≤ ${LIMITS.DISPLAY_NAME_MAX} characters.`);
      return;
    }
    setCreatingSession(true);
    setNameError(null);
    try {
      await createSession(trimmed);
      // WHY: if we were redirected from a room page, navigate back to it
      // now that the user has a session. Otherwise stay on the lobby.
      if (returnTo && returnTo.startsWith('/room/')) {
        navigate(returnTo, { replace: true });
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to start session.';
      toast.error(message);
      setNameError(message);
    } finally {
      setCreatingSession(false);
    }
  }

  // ── Step 2a: create a room for the selected media ──────────────────────────
  async function handleCreateRoom(e: FormEvent) {
    e.preventDefault();
    if (!selectedMedia) {
      toast.error('Pick a media item first.');
      return;
    }
    const trimmedName = roomName.trim() || selectedMedia.title;
    setCreatingRoom(true);
    try {
      const res = await postRoom(
        {
          mediaId: selectedMedia.id,
          name: trimmedName,
        },
        adminToken ?? undefined,
      );
      setRoom({
        room: res.room,
        role: res.role,
        // WHY: the create response (PostRoomsResponse) omits media detail; we
        // seed only the summary and let RoomPage re-join to fetch the full
        // MediaDetail and mint the sr_media cookie.
        mediaDetail: null,
        joinCode: res.joinCode,
        hostKey: res.hostKey,
      });
      // WHY: surface the invitation code so the admin can share it with
      // normal users, who enter it on the root landing page to join.
      setLastInvitationCode(res.invitationCode);
      toast.success(`Room created. Invitation code: ${res.invitationCode}`);
      navigate(`/room/${res.room.id}`);
    } catch (err) {
      if (err instanceof AppError && err.code === ErrorCode.E_ADMIN_KEY_REQUIRED) {
        clearAdminToken();
        setAdminError('Admin key required to create rooms. Re-enter it below.');
        toast.error('Admin key required to create rooms.');
      } else {
        const message = err instanceof Error ? err.message : 'Failed to create room.';
        toast.error(message);
      }
    } finally {
      setCreatingRoom(false);
    }
  }

  // ── Admin token verification ───────────────────────────────────────────────
  async function handleVerifyAdmin(e: FormEvent) {
    e.preventDefault();
    const trimmed = adminKeyInput.trim();
    if (!trimmed) {
      setAdminError('Enter an admin key.');
      return;
    }
    setVerifyingAdmin(true);
    setAdminError(null);
    try {
      await verifyAdminToken(trimmed);
      setAdminToken(trimmed);
      toast.success('Admin verified.');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Invalid admin key.';
      setAdminError(message);
      toast.error(message);
    } finally {
      setVerifyingAdmin(false);
    }
  }

  // ── Step 2b: join by code ─────────────────────────────────────────────────
  function handleJoinByCode(e: FormEvent) {
    e.preventDefault();
    const code = joinCode.trim();
    if (!code) {
      toast.error('Enter a room code.');
      return;
    }
    // WHY: the frozen REST contract has no joinCode→roomId resolver. We navigate
    // to /room/<code> and let RoomPage attempt POST /api/rooms/:id/join; if the
    // server treats the join code as a room alias it resolves, otherwise the
    // user sees a not-found toast. See DONE-WS-5.md (integration request).
    logger.debug('join by code', { code });
    navigate(`/room/${encodeURIComponent(code)}`);
  }

  // ── Identity gate ─────────────────────────────────────────────────────────
  if (!token || !user) {
    return (
      <AuthShell headline="Who’s watching?" subline="Pick a name to get started.">
        <form className={authStyles.form} onSubmit={handleCreateSession}>
          <div className="field">
            <label className="field__label" htmlFor="displayName">
              Display name
            </label>
            <input
              id="displayName"
              className={`input ${nameError ? 'input--error' : ''}`}
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              maxLength={LIMITS.DISPLAY_NAME_MAX}
              placeholder="Your name"
              autoComplete="off"
              autoFocus
              aria-invalid={nameError ? true : undefined}
              aria-describedby={nameError ? 'displayNameError' : undefined}
            />
            {nameError ? (
              <span id="displayNameError" className={authStyles.error}>
                {nameError}
              </span>
            ) : null}
          </div>
          <Button type="submit" variant="primary" loading={creatingSession} className={authStyles.submit}>
            Continue
          </Button>
        </form>
      </AuthShell>
    );
  }

  // ── Admin token gate ─────────────────────────────────────────────────────
  // WHY: only verified admins see the catalogue and can create rooms. The
  // admin token is verified server-side (GET /api/admin/jobs) and persisted
  // in the session store so it survives reloads in the same tab.
  if (!adminToken) {
    return (
      <AuthShell
        headline="Admin console"
        subline="Enter your admin key to manage the library and host rooms."
        footer={
          <>
            Not an admin? <a href="/">Join with an invitation code</a>
          </>
        }
      >
        <form className={authStyles.form} onSubmit={handleVerifyAdmin}>
          <div className="field">
            <label className="field__label" htmlFor="adminKey">
              Admin key
            </label>
            <input
              id="adminKey"
              className={`input ${adminError ? 'input--error' : ''}`}
              type="password"
              value={adminKeyInput}
              onChange={(e) => setAdminKeyInput(e.target.value)}
              placeholder="Admin key"
              autoComplete="off"
              autoFocus
              aria-invalid={adminError ? true : undefined}
              aria-describedby={adminError ? 'adminKeyError' : undefined}
            />
            {adminError ? (
              <span id="adminKeyError" className={authStyles.error}>
                {adminError}
              </span>
            ) : null}
          </div>
          <Button type="submit" variant="primary" loading={verifyingAdmin} className={authStyles.submit}>
            Verify
          </Button>
        </form>
      </AuthShell>
    );
  }

  // ── Authenticated lobby ───────────────────────────────────────────────────
  // Layout: sticky top nav → billboard (selected title + create-room) → live
  // rooms → poster library. Picking a poster scrolls the billboard into view,
  // so "choose a title, start a room" is a two-click flow.
  return (
    <div className={styles.lobby}>
      <header className={styles.nav}>
        <div className={styles.navLeft}>
          <Wordmark size="md" />
          <span className={styles.navTag}>Admin</span>
        </div>
        <div className={styles.navRight}>
          <form className={styles.joinForm} onSubmit={handleJoinByCode}>
            <input
              className={`input ${styles.joinInput}`}
              value={joinCode}
              onChange={(e) => setJoinCode(e.target.value)}
              maxLength={JOIN_INPUT_MAX}
              placeholder="Room code"
              autoComplete="off"
              aria-label="Room code"
            />
            <Button type="submit" variant="secondary">
              Join
            </Button>
          </form>
          <span className={styles.userChip} title={user.displayName}>
            <span className={styles.avatar} aria-hidden="true">
              {user.displayName.slice(0, 1).toUpperCase()}
            </span>
            <span className={styles.userName}>{user.displayName}</span>
          </span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              clearAdminToken();
              navigate('/');
            }}
          >
            Exit admin
          </Button>
        </div>
      </header>

      <section className={styles.hero} aria-label="Create a room">
        {selectedMedia?.posterUrl ? (
          <img className={styles.heroBackdrop} src={selectedMedia.posterUrl} alt="" />
        ) : null}
        <div className={styles.heroShade} aria-hidden="true" />
        <div className={styles.heroBody} key={selectedMedia?.id ?? 'empty'}>
          {selectedMedia ? (
            <>
              <span className={styles.heroKicker}>Ready to host</span>
              <h1 className={styles.heroTitle}>{selectedMedia.title}</h1>
              <p className={styles.heroMeta}>{formatDuration(selectedMedia.durationSec)}</p>
              <form className={styles.heroForm} onSubmit={handleCreateRoom}>
                <input
                  id="roomName"
                  className={`input ${styles.heroInput}`}
                  value={roomName}
                  onChange={(e) => setRoomName(e.target.value)}
                  maxLength={LIMITS.ROOM_NAME_MAX}
                  placeholder={`Room name (default: ${selectedMedia.title})`}
                  autoComplete="off"
                  aria-label="Room name"
                />
                <Button type="submit" variant="primary" loading={creatingRoom} className={styles.heroCta}>
                  ▶&nbsp; Start watch party
                </Button>
                <Button type="button" variant="ghost" onClick={() => setSelectedMedia(null)}>
                  Clear
                </Button>
              </form>
            </>
          ) : (
            <>
              <span className={styles.heroKicker}>Syncreel</span>
              <h1 className={styles.heroTitle}>What are we watching tonight?</h1>
              <p className={styles.heroHint}>
                Pick a title from your library below. Everyone who joins stays in lock-step with you.
              </p>
            </>
          )}
          {lastInvitationCode ? (
            <div className={styles.invitationCodeBox}>
              <span className={styles.invitationCodeLabel}>Last invitation code — share with viewers</span>
              <code className={styles.invitationCodeValue}>{lastInvitationCode}</code>
            </div>
          ) : null}
        </div>
      </section>

      <div className={styles.content}>
        {activeRooms.length > 0 ? (
          <section>
            <div className="sectionHeading">
              <h2>Live rooms</h2>
              <span className="sectionHeadingNote">{activeRooms.length} running</span>
            </div>
            <div className={styles.activeRooms}>
              {activeRooms.map((r) => (
                <div key={r.id} className={styles.activeRoom}>
                  <div className={styles.activeRoomInfo}>
                    <span className={styles.liveTag}>
                      <span className={styles.activeRoomDot} />
                      Live
                    </span>
                    <span className={styles.activeRoomName} title={r.name}>
                      {r.name}
                    </span>
                    <span className={styles.activeRoomMeta}>
                      {r.memberCount} member{s(r.memberCount)} · created {timeAgo(r.createdAt)}
                    </span>
                    {r.hostKey ? (
                      <span className={styles.activeRoomMeta}>
                        Host key{' '}
                        <code className={styles.hostKeyValue}>{r.hostKey}</code>{' '}
                        <button
                          type="button"
                          className={styles.hostKeyCopy}
                          onClick={() => handleCopyHostKey(r.hostKey as string)}
                        >
                          Copy
                        </button>
                      </span>
                    ) : null}
                  </div>
                  <div className={styles.activeRoomActions}>
                    {r.hostKey ? (
                      <Button type="button" variant="primary" size="sm" onClick={() => handleTakeOver(r)}>
                        Take over
                      </Button>
                    ) : null}
                    <Button type="button" variant="secondary" size="sm" onClick={() => navigate(`/room/${r.id}`)}>
                      Rejoin
                    </Button>
                    <Button
                      type="button"
                      variant="danger"
                      size="sm"
                      loading={terminatingId === r.id}
                      disabled={terminatingId !== null}
                      onClick={(e) => handleTerminateRoom(e, r.id)}
                    >
                      End
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        ) : null}

        <section>
          <div className="sectionHeading">
            <h2>Your library</h2>
          </div>
          <AddMedia onChanged={() => setCatalogRefresh((n) => n + 1)} />
          <LibraryBrowser onChanged={() => setCatalogRefresh((n) => n + 1)} />
          <MediaCatalog
            selectedId={selectedMedia?.id}
            onSelect={(media) => {
              setSelectedMedia(media);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            refreshKey={catalogRefresh}
          />
        </section>
      </div>
    </div>
  );
}

/** Pluralize "member" / "members". */
function s(n: number): string {
  return n === 1 ? '' : 's';
}

/** Format a relative time string like "2m ago". */
function timeAgo(ts: number): string {
  const sec = Math.floor((Date.now() - ts) / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.floor(hr / 24)}d ago`;
}
